/** Tabbed interface for organizing KPIs by category (Financial, Quality, Volume). */
import React, { ReactNode } from "react";
import { Box, Tabs, Tab, SxProps } from "@mui/material";
import {
  AttachMoney as FinancialIcon,
  Speed as QualityIcon,
  PhoneInTalk as VolumeIcon,
} from "@mui/icons-material";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

/** Helper component to display tab content. */
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kpi-tabpanel-${index}`}
      aria-labelledby={`kpi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/** Accessibility props for tabs. */
function a11yProps(index: number) {
  return {
    id: `kpi-tab-${index}`,
    "aria-controls": `kpi-tabpanel-${index}`,
  };
}

interface KPITabsProps {
  financialContent: ReactNode;
  qualityContent: ReactNode;
  volumeContent: ReactNode;
  onTabChange?: (tabIndex: number, tabName: string) => void;
  sx?: SxProps;
}

/** Tabbed interface for KPI categories with Financial, Quality, Volume tabs. */
export default function KPITabs({
  financialContent,
  qualityContent,
  volumeContent,
  onTabChange,
  sx,
}: KPITabsProps) {
  const [value, setValue] = React.useState(0);

  const tabs = [
    { label: "Financial", icon: <FinancialIcon /> },
    { label: "Quality", icon: <QualityIcon /> },
    { label: "Volume", icon: <VolumeIcon /> },
  ];

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (onTabChange) {
      const tabNames = ["financial", "quality", "volume"];
      onTabChange(newValue, tabNames[newValue]);
    }
  };

  return (
    <Box sx={sx}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Business KPIs tabs"
          variant="fullWidth"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              {...a11yProps(index)}
              sx={{
                minHeight: 64,
                fontSize: "1rem",
                fontWeight: 500,
              }}
            />
          ))}
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        {financialContent}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {qualityContent}
      </TabPanel>
      <TabPanel value={value} index={2}>
        {volumeContent}
      </TabPanel>
    </Box>
  );
}
